import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CARD_SECTION_TITLE_CLASS, ConfirmModal, TextButton } from '@safetyhub/ui';
import { PERMISSION_GROUP_LABELS, groupPermissions } from '@safetyhub/shared';
import { MODAL_LAYER } from '../../../components/modalLayers';
import { LockIcon } from '../../../components/icons';
import { DirtyHint } from '../../../components/common/DirtyHint';
import { useToastStore } from '../../../stores/toast';
import {
  rbacApi,
  type CompanyRoleView,
  type CreateCompanyRoleDto,
  type PermissionDefinitionView,
} from '../../../lib/rbac';
import { CreateRoleModal } from './CreateRoleModal';
import { rbacErrorMessage } from './rbacErrors';

type Draft = Record<string, Set<string>>;

function draftFromRoles(roles: CompanyRoleView[]): Draft {
  const draft: Draft = {};
  for (const r of roles) draft[r.key] = new Set(r.permissionKeys);
  return draft;
}

function sameSet(a: Set<string>, b: string[]): boolean {
  if (a.size !== b.length) return false;
  for (const k of b) if (!a.has(k)) return false;
  return true;
}

// 잠긴 행 표식(`orgAdjustable=false` — 시스템 역할 값이 항상 이기는 키, 결정 §3)은 공용
// `components/icons`의 `LockIcon`이다. 결재라인 편집 모달의 잠긴 칸과 같은 뜻·같은 path다.

const LOCKED_TITLE = '시스템이 정한 권한입니다. 조직에서 켜거나 끌 수 없습니다.';

type PendingConfirm =
  | { kind: 'reset'; role: CompanyRoleView }
  | { kind: 'delete'; role: CompanyRoleView };

interface PermissionMatrixSectionProps {
  companyId: string;
  permissions: PermissionDefinitionView[];
  roles: CompanyRoleView[];
  onChanged: () => Promise<void>;
}

/**
 * ③ 역할·권한 매트릭스 — 행=권한(group별), 열=역할. 체크 변경은 **열(역할) 단위**로 모아 저장하고
 * dirty 표준(결정 `2026-08-16-edit-modal-dirty-gate-standard`)을 열마다 적용한다(저장 버튼 `!isDirty` 게이트 +
 * 「변경사항 없음/있습니다」). SYSTEM 역할의 권한 변경은 오버라이드 행 생성(PATCH `permissionKeys`만), `overridden`이면
 * 「기본값으로」(DELETE override). COMPANY 역할은 보유자 0일 때만 삭제.
 *
 * 소유자 역할(`isOwner`)은 전체 권한이 고정이라 열을 읽기 전용으로 둔다.
 */
export function PermissionMatrixSection({ companyId, permissions, roles, onChanged }: PermissionMatrixSectionProps) {
  const { showToast } = useToastStore();
  const [draft, setDraft] = useState<Draft>(() => draftFromRoles(roles));
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // 서버 정본이 바뀌면(저장·되돌리기·생성·삭제 뒤 재조회) 초안을 정본으로 되맞춘다.
  useEffect(() => {
    setDraft(draftFromRoles(roles));
  }, [roles]);

  const groups = useMemo(() => groupPermissions(permissions), [permissions]);
  const orderedRoles = useMemo(() => roles.slice().sort((a, b) => a.sortOrder - b.sortOrder), [roles]);

  const isColumnDirty = (role: CompanyRoleView) => {
    const set = draft[role.key];
    return set ? !sameSet(set, role.permissionKeys) : false;
  };

  const toggle = (roleKey: string, permissionKey: string) => {
    setDraft((prev) => {
      const next = { ...prev, [roleKey]: new Set(prev[roleKey] ?? []) };
      if (next[roleKey].has(permissionKey)) next[roleKey].delete(permissionKey);
      else next[roleKey].add(permissionKey);
      return next;
    });
  };

  const handleSave = async (role: CompanyRoleView) => {
    if (!isColumnDirty(role) || savingKey) return;
    setSavingKey(role.key);
    try {
      await rbacApi.updateRole(companyId, role.key, { permissionKeys: Array.from(draft[role.key] ?? []) });
      showToast(`「${role.name}」 권한을 저장했습니다.`, 'success');
      await onChanged();
    } catch (err) {
      showToast(rbacErrorMessage(err, '권한 저장에 실패했습니다.'), 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setIsConfirming(true);
    try {
      if (pending.kind === 'reset') {
        await rbacApi.resetRoleOverride(companyId, pending.role.key);
        showToast(`「${pending.role.name}」 권한을 기본값으로 되돌렸습니다.`, 'success');
      } else {
        await rbacApi.deleteRole(companyId, pending.role.key);
        showToast(`「${pending.role.name}」 역할을 삭제했습니다.`, 'success');
      }
      setPending(null);
      await onChanged();
    } catch (err) {
      showToast(rbacErrorMessage(err, pending.kind === 'reset' ? '되돌리기에 실패했습니다.' : '삭제에 실패했습니다.'), 'error');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCreate = async (payload: CreateCompanyRoleDto) => {
    setIsCreating(true);
    try {
      await rbacApi.createRole(companyId, payload);
      showToast(`「${payload.name}」 역할을 만들었습니다.`, 'success');
      setShowCreate(false);
      await onChanged();
    } catch (err) {
      showToast(rbacErrorMessage(err, '역할 생성에 실패했습니다.'), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>역할·권한</h2>
          <p className="text-xs text-ink-500">
            역할별로 권한을 켜고 끕니다. 잠긴 행은 시스템이 정한 값이라 조직에서 바꿀 수 없습니다.
          </p>
        </div>
        <Button type="button" size="sm" variant="primary" onClick={() => setShowCreate(true)}>
          새 역할
        </Button>
      </div>

      {/*
        열 수가 많아 모바일 폭(≤390px)에서는 반드시 넘친다 — 표 자체를 가로 스크롤 컨테이너에 둔다.
        ⚠️ 여기에 가로 padding(`-mx-2 px-2`)을 두면 **고정 열 아래로 내용이 비친다**: 스크롤 내용은
        border box(클립 경계)까지 보이는데 `sticky left-0`은 padding 안쪽에 서므로 그 패딩 폭만큼
        덮이지 않는 띠가 생긴다(실측: 클립 297 · 고정 셀 305 = 8px). 고정 열이 있는 스크롤 컨테이너는
        가로 padding을 두지 않는다 — 필요하면 바깥(Card)에서 준다.

        ⚠️ 세로 고정(`thead`)에는 **높이 제한이 함께 있어야 한다.** `overflow-x-auto`만 두면 CSS가
        `overflow-y`를 `visible`로 두지 못해 `auto`로 계산하는데(한 축이 visible이 아니면 다른 축도
        따라간다), 그러면 이 컨테이너가 **세로 스크롤의 주체**가 된다. 정작 스크롤되는 것은 페이지라
        컨테이너 스크롤량은 늘 0이고 `sticky top-0`은 아무 일도 하지 않는다(실측: clientH=scrollH=2041).
        그래서 `max-h`로 **표 자체가 세로로 스크롤되게** 만들어야 헤더 고정이 뜻을 갖는다.
      */}
      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full text-sm" data-testid="permission-matrix">
          <thead>
            <tr>
              {/* 모서리 칸 — 가로·세로 둘 다 고정이라 z가 가장 높아야 양쪽에서 덮는다 */}
              <th className="sticky left-0 top-0 z-[3] border-b border-line bg-white px-3 py-2 text-left text-xs font-semibold text-ink-500 min-w-[180px]">
                권한
              </th>
              {orderedRoles.map((role) => (
                <th
                  key={role.key}
                  className="sticky top-0 z-[2] border-b border-line bg-white px-2 py-2 text-center align-bottom min-w-[96px]"
                  scope="col"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs font-semibold text-ink-900 whitespace-nowrap">{role.name}</span>
                    {role.scope === 'COMPANY' ? (
                      <Badge tone="violet" shape="rounded" size="sm">
                        조직
                      </Badge>
                    ) : role.overridden ? (
                      <Badge tone="amber" shape="rounded" size="sm">
                        변경됨
                      </Badge>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {groups.map(({ group, items }) => (
              <GroupRows
                key={group}
                group={group}
                items={items}
                roles={orderedRoles}
                draft={draft}
                savingKey={savingKey}
                onToggle={toggle}
              />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line">
              <td className="sticky left-0 z-[1] bg-white px-3 py-2 text-xs text-ink-500">저장 / 관리</td>
              {orderedRoles.map((role) => {
                const dirty = isColumnDirty(role);
                const saving = savingKey === role.key;
                return (
                  <td key={role.key} className="px-2 py-2 align-top" data-role-column={role.key}>
                    <div className="flex flex-col items-center gap-1">
                      {!role.isOwner && (
                        <>
                          <Button
                            type="button"
                            size="xs"
                            variant="primary"
                            onClick={() => handleSave(role)}
                            disabled={!dirty || !!savingKey}
                          >
                            {saving ? '저장 중' : '저장'}
                          </Button>
                          <DirtyHint isDirty={dirty} className="text-[11px] whitespace-nowrap" />
                        </>
                      )}
                      {role.scope === 'SYSTEM' && role.overridden && (
                        <TextButton
                          tone="neutral"
                          type="button"
                          className="text-[11px] px-1 whitespace-nowrap"
                          onClick={() => setPending({ kind: 'reset', role })}
                        >
                          기본값으로
                        </TextButton>
                      )}
                      {role.scope === 'COMPANY' && (
                        <TextButton
                          tone="danger"
                          type="button"
                          className="text-[11px] px-1 whitespace-nowrap"
                          onClick={() => setPending({ kind: 'delete', role })}
                          disabled={role.holderCount > 0}
                          title={role.holderCount > 0 ? '맡은 인원이 있어 삭제할 수 없습니다' : undefined}
                        >
                          삭제
                        </TextButton>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      <CreateRoleModal
        open={showCreate}
        permissions={permissions}
        isSubmitting={isCreating}
        overlayZClassName={MODAL_LAYER.root}
        onSubmit={handleCreate}
        onClose={() => {
          if (!isCreating) setShowCreate(false);
        }}
      />

      <ConfirmModal
        open={pending !== null}
        title={pending?.kind === 'delete' ? '역할 삭제' : '기본값으로 되돌리기'}
        body={
          pending?.kind === 'delete'
            ? `「${pending.role.name}」 역할을 삭제합니다. 되돌릴 수 없습니다.`
            : pending
              ? `「${pending.role.name}」의 조직 변경분을 버리고 시스템 기본 권한으로 되돌립니다.`
              : undefined
        }
        confirmLabel={pending?.kind === 'delete' ? '삭제' : '되돌리기'}
        danger
        busy={isConfirming}
        overlayZClassName={MODAL_LAYER.root}
        onConfirm={handleConfirm}
        onClose={() => {
          if (!isConfirming) setPending(null);
        }}
      />
    </Card>
  );
}

function GroupRows({
  group,
  items,
  roles,
  draft,
  savingKey,
  onToggle,
}: {
  group: string;
  items: PermissionDefinitionView[];
  roles: CompanyRoleView[];
  draft: Draft;
  savingKey: string | null;
  onToggle: (roleKey: string, permissionKey: string) => void;
}) {
  return (
    <>
      <tr className="bg-surface-soft">
        {/*
          그룹 라벨은 `colSpan`으로 표 폭 전체를 덮는 띠다 — 그래서 **셀에** `sticky left-0`을 붙이면 무효다
          (셀이 이미 x=0에서 시작하고 표만큼 넓어 붙을 곳이 없다. 라벨 텍스트는 셀 안쪽 시작점에 있으므로
          가로 스크롤에 그대로 밀려 나간다). 고정할 것은 **셀이 아니라 셀 안의 shrink-to-fit 요소**다 —
          그것이 띠 안에서 움직일 수 있어야 스크롤 중에도 왼쪽에 남는다. 첫 열 셀들(아래 `td`)과 같은 x=0에 선다.
        */}
        <th colSpan={roles.length + 1} scope="rowgroup" className="p-0 text-left">
          <div className="sticky left-0 inline-block bg-surface-soft px-3 py-1.5 text-xs font-semibold text-ink-500">
            {PERMISSION_GROUP_LABELS[group] ?? group}
          </div>
        </th>
      </tr>
      {items.map((p) => (
        <tr key={p.key} data-permission-key={p.key} data-locked={p.orgAdjustable ? undefined : 'true'}>
          <td className="sticky left-0 z-[1] bg-white px-3 py-2 min-w-[180px]">
            <div className="flex items-center gap-1.5">
              <span className="text-ink-900">{p.label}</span>
              {!p.orgAdjustable && (
                <span title={LOCKED_TITLE} aria-label="잠김" className="inline-flex text-ink-400">
                  <LockIcon className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
            {p.description && <p className="text-[11px] text-ink-400">{p.description}</p>}
          </td>
          {roles.map((role) => {
            const checked = draft[role.key]?.has(p.key) ?? false;
            const disabled = !p.orgAdjustable || role.isOwner || savingKey === role.key;
            return (
              <td key={role.key} className="px-2 py-2 text-center">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(role.key, p.key)}
                  aria-label={`${role.name} · ${p.label}`}
                  title={!p.orgAdjustable ? LOCKED_TITLE : undefined}
                  className="h-4 w-4 rounded border-line text-brand-600 disabled:opacity-50"
                />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
